import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';

// Drawing canvas used inside OnsiteSignatureDialog (IGFEMS-585). Starts blank; reports the
// drawing as a PNG data URL ('' when cleared). The dialog's Clear button calls ref.clear().
//
// The bitmap is always SIG_WIDTH × SIG_HEIGHT; CSS only scales it on screen. So every saved
// signature has the same shape, size and line weight whatever device it was drawn on.
export const SIG_WIDTH  = 1000;
export const SIG_HEIGHT = 400;
const LINE_WIDTH = 4; // in bitmap pixels

const OnsiteSignaturePad = forwardRef(({ onChange, maxHeight }, ref) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInkRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  const notifyParent = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !onChange) return;
    onChange(hasInkRef.current ? canvas.toDataURL('image/png') : '');
  }, [onChange]);

  // Screen point → bitmap point. Clamped so a stroke that leaves the pad keeps drawing
  // along its edge (IGFEMS-602).
  const pos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width  / (rect.width  || canvas.width);
    const scaleY = canvas.height / (rect.height || canvas.height);
    return {
      x: Math.min(Math.max(e.clientX - rect.left, 0), rect.width)  * scaleX,
      y: Math.min(Math.max(e.clientY - rect.top,  0), rect.height) * scaleY,
    };
  };

  const start = (e) => {
    // preventDefault also stops the compat mouse events (see the dom-autoscroller note in end()).
    e.preventDefault();
    // One pointer only: ignore a resting palm / second finger and right/middle mouse clicks.
    if (drawing.current || e.isPrimary === false || e.button > 0) return;
    // Pointer capture: moves outside the canvas still come here, so the stroke never "sticks" at the border.
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!drawing.current || e.isPrimary === false) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pos(e);
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = LINE_WIDTH;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y);
    if (!hasInkRef.current) { hasInkRef.current = true; setHasInk(true); }
  };

  const end = (e) => {
    if (e?.preventDefault) e.preventDefault();
    // Stop bubbling to window so dom-autoscroller's onUp handler doesn't fire
    // and trigger its broken animationFramePolyfill.cancelAnimationFrame call
    if (e?.nativeEvent) e.nativeEvent.stopPropagation();
    if (drawing.current && e?.isPrimary !== false) {
      drawing.current = false;
      notifyParent();
    }
  };

  useImperativeHandle(ref, () => ({
    clear: () => {
      canvasRef.current?.getContext('2d')?.clearRect(0, 0, SIG_WIDTH, SIG_HEIGHT);
      hasInkRef.current = false;
      setHasInk(false);
      onChange?.('');
    },
  }), [onChange]);

  // Width-limited by the dialog, and height-limited by maxHeight while keeping the 5:2 shape.
  const style = maxHeight ? { maxWidth: (maxHeight * SIG_WIDTH) / SIG_HEIGHT } : undefined;

  return (
    <div className="onsite-sig-pad" style={style}>
      <canvas
        ref={canvasRef}
        width={SIG_WIDTH}
        height={SIG_HEIGHT}
        role="img"
        aria-label="Signature pad"
        className="onsite-sig-pad__canvas"
        onPointerDown={start}
        onPointerMove={draw}
        onPointerUp={end}
        onPointerCancel={end}
      />
      {!hasInk && (
        <div className="onsite-sig-pad__hint">
          <i className="fa-solid fa-pen-nib" aria-hidden="true" />Sign here
        </div>
      )}
    </div>
  );
});

export default OnsiteSignaturePad;
